import BlockWrapper from 'Components/_NewElements/BlockWrapper';
import RoundBox from 'Components/_NewElements/RoundBox';
import ConnectedPartialDate from 'Components/InputHandler/components/PartialDate';
import * as React from 'react';
import {
  FilterField,
  hasPatientData,
  HeaderTimeDisplay,
  ItemStatus,
  MessageDetailRow,
  MessageLayout,
  openPDFOnclick,
  TAttachmentData,
} from '../../../components/Messaging/components';
import { Container, Item } from 'Components/Grid';
import { partialDateFromDate } from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import colors from '../../../config/theme/colors';
import { useIntl } from 'react-intl';
import { useAppDispatch, useAppSelector } from 'Store/index';
import { actions as messagingActions } from 'Store/messaging';
import { TTOPIC } from 'Store/messaging/types/TTopic';
import CircularProgressLocal from 'Components/CircularProgress';
import { topicFetchBuilder } from 'Store/messaging/fetchers/topics';
import PatientList from 'Components/Messaging/components/PatientList';
import { platforms } from '../../../config/platformSettings';
import { intersperse } from 'ramda';
import GroupMessageForm from 'Components/Messaging/components/GroupMessageForm';
import ThemedDialog from 'Components/_NewElements/Dialog';
import ConfirmationDialog from 'Components/ConfirmationDialog';
import { attachmentFetchBuilder } from 'Store/messaging/fetchers/attachment';
import AttachmentDialog from 'Components/Messaging/components/AttachmentDialog';

const GroupMessageEditingDialog = ({
  topic,
  open,
  setEditing,
  fm,
  reloadTopics,
  existingAttachments,
  deleteAttachment,
}: IGroupMessageEditingDialog) => {
  const [newFormData, setNewFormData] = React.useState<{ title?: string; body?: string }>({});
  const formData = { title: topic.title, body: topic.body, ...newFormData };

  const onChange = (name: string) => (values: TOnChangeValues) => {
    const value = values?.[name];
    setNewFormData({ ...newFormData, [name]: value });
  };

  const cancelEditing = () => {
    setEditing(false);
  };

  const [newAttachmentData, setNewAttachmentData] = React.useState<File[]>([]);
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setNewAttachmentData([...(newAttachmentData || []), e.target.files[0]]);
    }
  };

  // Loading indicator for when you save a topic
  const [loadingSend, setLoadingSend] = React.useState<boolean>(false);

  const sendUpdatedTopic = async () => {
    const title = newFormData.title;
    const body = newFormData.body;
    setLoadingSend(true);
    if (title || body) {
      await topicFetchBuilder<'update'>({ name: 'update', body: { ...formData }, queryParams: [topic.id] });
    }
    if (newAttachmentData) {
      newAttachmentData.forEach(async att => {
        await attachmentFetchBuilder<'createTopic'>({
          name: 'createTopic',
          queryParams: [topic.id],
          body: att,
          contentType: att.type,
        });
      });
    }
    setLoadingSend(false);
    reloadTopics();
  };

  return (
    <ThemedDialog open={open} title={fm('messaging.editGroupMessage')}>
      {loadingSend ? (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgressLocal size={80} />
        </div>
      ) : (
        <GroupMessageForm
          topic={topic}
          formValues={formData}
          onChange={onChange}
          cancelGroupMessage={cancelEditing}
          sendGroupMessage={sendUpdatedTopic}
          attachmentData={newAttachmentData}
          handleFileChange={handleFileChange}
          existingAttachments={existingAttachments}
          deleteAttachment={deleteAttachment}
        />
      )}
    </ThemedDialog>
  );
};

interface IGroupMessageEditingDialog {
  topic: TTOPIC;
  open: boolean;
  setEditing: (open: boolean) => void;
  fm: (id: string) => string;
  reloadTopics: () => void;
  existingAttachments?: Record<string, TAttachmentData> | null;
  deleteAttachment: (id: string) => void;
}

const GroupMessageHeader = ({ t, fm, color }: { t: TTOPIC; fm: (id: string) => string; color?: 'gray' | 'yellow' }) => {
  const platformFormat = (value: string) => {
    if (platforms.includes(value as Platform)) return fm('header.platforms.' + value);
    else return value;
  };
  const tags = (t.tags || []).map(tag => platformFormat(tag));

  return (
    <Container>
      <Item xs={9}>
        <div style={{ fontSize: '1.8rem' }}>{t.title}</div>
        <Container>
          <Item xs={7} md={4.6}>
            <HeaderTimeDisplay date={t.creationTimestamp} color={color} />
          </Item>
          <Item xs={true}>
            <ItemStatus item={t} fm={fm} />
          </Item>
        </Container>
      </Item>
      <Item xs={3} style={{ paddingRight: '1rem' }}>
        <div style={{ fontSize: '1.6rem', fontWeight: 600, display: 'flex', justifyContent: 'flex-end' }}>
          {t.targetPatients.length} {fm('messaging.recipients')}
        </div>
        <div style={{ fontSize: '1.6rem', fontWeight: 600, display: 'flex', justifyContent: 'flex-end' }}>
          {intersperse(';', tags)}
        </div>
      </Item>
    </Container>
  );
};

const GroupMessageOpen = ({ t: topic, fm, reloadTopics }: IGroupMessageOpen) => {
  const timedate = (timestamp: number) => new Date(timestamp);
  const minutes = (timedate: Date) =>
    `${timedate.getMinutes().toString().length === 1 ? 0 : ''}${timedate.getMinutes()}`;

  const [topicData, setTopicData] = React.useState<TTOPIC | null>(null);
  const [attachmentsData, setAttachmentsData] = React.useState<Record<string, TAttachmentData> | null>(null);

  const getTopicData = () => {
    const firstPatientId = typeof topic.targetPatients[0] === 'string' && topic.targetPatients[0];
    firstPatientId &&
      void topicFetchBuilder<'fetch'>({ name: 'fetch', queryParams: [firstPatientId, topic.id] }).then(async res => {
        if (res) {
          setTopicData(res);
          if (res?.attachments) {
            const thumbOrAttachFetches: { id: string; blob: File; type: string }[] = [];
            for (let index = 0; index < res?.attachments.length; index++) {
              const att = res?.attachments[index];
              const fetch =
                att.contentMimeType === 'application/pdf'
                  ? await thumbnailOrAttachFetch(att.id, 'undefined', true, true)
                  : att.thumbnails?.[0]?.id
                    ? await thumbnailOrAttachFetch(att.id, att.thumbnails?.[0]?.id, false, false)
                    : await thumbnailOrAttachFetch(att.id, 'undefined', true, false); // No thumbnail for image

              fetch && thumbOrAttachFetches.push({ id: fetch.id, type: fetch.type, blob: fetch.file });
            }

            let atData = {};
            thumbOrAttachFetches.forEach(attachment => {
              atData = { ...atData, [attachment.id]: { type: attachment.type, blob: attachment.blob } };
            });
            setAttachmentsData(atData);
          }
        }
      });
  };
  React.useEffect(() => {
    getTopicData();
  }, [topic]);

  const [editingOpen, setEditingOpen] = React.useState<boolean>(false);

  const completeTopicToggle = async () => {
    if (topic.closureTimestamp) {
      await topicFetchBuilder<'unclose'>({ name: 'unclose', queryParams: [topic.id] });
    } else {
      await topicFetchBuilder<'close'>({ name: 'close', queryParams: [topic.id, `${Date.now()}`] });
    }
    reloadTopics();
  };

  const [deleteConfirmation, setDeleteConfirmation] = React.useState<TTOPIC | null>(null);
  const deleteTopic = (topic: TTOPIC) => {
    setDeleteConfirmation(topic);
  };

  const thumbnailOrAttachFetch = async (aid: string, tid: string, attach?: boolean, pdf?: boolean) => {
    const firstPid = hasPatientData(topic.targetPatients) ? topic.targetPatients[0].id : topic.targetPatients[0];
    if (!firstPid || typeof firstPid !== 'string') return;
    const file = attach
      ? await attachmentFetchBuilder<'fetch'>({
          name: 'fetch',
          queryParams: [firstPid, aid],
        })
      : await attachmentFetchBuilder<'fetchThumb'>({
          name: 'fetchThumb',
          queryParams: [firstPid, aid, tid],
        });

    return { id: aid, file, type: pdf ? 'pdf' : 'image' };
  };

  const [openAttachmentId, setOpenAttachmentId] = React.useState<string | null>(null);

  const deleteAttachment = (id: string) => {
    void attachmentFetchBuilder<'delete'>({ name: 'delete', queryParams: [id, undefined] }).then(res => {
      if (res) {
        const newAttachments = { ...attachmentsData };
        delete newAttachments[id];
        setAttachmentsData(newAttachments);
      }
    });
  };

  return (
    <>
      <MessageLayout
        buttons={[
          { text: 'messaging.editGroupMessage', onClick: () => setEditingOpen(true) },
          {
            text: topic.closureTimestamp ? 'messaging.markUncompleteGroup' : 'messaging.markCompleteGroup',
            onClick: completeTopicToggle,
            colorScheme: 'warning',
          },
          {
            text: 'messaging.deleteGroupMessage',
            onClick: () => deleteTopic(topic),
            colorScheme: 'error',
          },
        ]}
      >
        <>
          <div>{topicData?.body}</div>
          <div style={{ margin: '2rem 0 0.5rem 0', color: colors.appBlue.default }}>
            {fm('messaging.groupMessageDetails')}
          </div>
          <MessageDetailRow
            title={fm('messaging.groupMessageSent')}
            value={
              <>
                <span>{`${formatPartialDate(partialDateFromDate(timedate(topic.creationTimestamp)))}`}</span>
                <span style={{ marginLeft: '1rem' }}>{`${timedate(topic.creationTimestamp).getHours()}:${minutes(
                  timedate(topic.creationTimestamp),
                )}`}</span>
              </>
            }
          />
          {topic.closureTimestamp && (
            <MessageDetailRow
              title={fm('messaging.groupMessageEnded')}
              value={
                <>
                  <span>{`${formatPartialDate(partialDateFromDate(timedate(topic.closureTimestamp)))}`}</span>
                  <span style={{ marginLeft: '1rem' }}>{`${timedate(topic.closureTimestamp).getHours()}:${minutes(
                    timedate(topic.closureTimestamp),
                  )}`}</span>
                </>
              }
            />
          )}
          <MessageDetailRow
            title={fm('messaging.sender')}
            value={topic.creatorLastNames + ' ' + topic.creatorFirstNames}
          />
          <MessageDetailRow
            title={fm('messaging.attachments')}
            value={
              attachmentsData && Object.keys(attachmentsData).length > 0 ? (
                <Container>
                  {Object.keys(attachmentsData).map(id => {
                    const thisAttachmentData: TAttachmentData | undefined = attachmentsData?.[id];
                    const imageDimensions =
                      thisAttachmentData?.type === 'image'
                        ? {
                            minHeight: '2rem',
                            minWidth: '2rem',
                            lineHeight: 0,
                            border: '0.1rem solid ' + colors.lightGray,
                            marginRight: '0.5rem',
                          }
                        : {};
                    return (
                      <Item
                        key={id}
                        onClick={
                          thisAttachmentData?.type === 'pdf'
                            ? () => openPDFOnclick(thisAttachmentData)
                            : () => setOpenAttachmentId(id)
                        }
                        style={{ cursor: 'pointer', marginRight: '1rem' }}
                      >
                        {
                          <div style={{ ...imageDimensions }}>
                            {thisAttachmentData?.type === 'image' ? (
                              <img
                                style={{ maxWidth: '100%', overflow: 'hidden' }}
                                src={URL.createObjectURL(thisAttachmentData.blob)}
                              />
                            ) : thisAttachmentData?.type === 'pdf' ? (
                              <div className="link">PDF</div>
                            ) : undefined}
                          </div>
                        }
                      </Item>
                    );
                  })}
                </Container>
              ) : undefined
            }
          />
          <MessageDetailRow
            title={fm('messaging.patientList')}
            value={topicData && <PatientList topic={topicData} />}
          />
        </>
      </MessageLayout>
      <GroupMessageEditingDialog
        open={editingOpen}
        setEditing={setEditingOpen}
        topic={{ ...topic, ...topicData }}
        fm={fm}
        reloadTopics={reloadTopics}
        existingAttachments={attachmentsData}
        deleteAttachment={deleteAttachment}
      />
      <ConfirmationDialog
        open={!!deleteConfirmation}
        text={fm('messaging.deleteDiscussionConfirmation')}
        cancel={{
          callback: e => {
            e.stopPropagation();
            setDeleteConfirmation(null);
          },
        }}
        confirm={{
          callback: async e => {
            e.stopPropagation();
            await topicFetchBuilder({ name: 'delete', queryParams: [topic.id] }).then(res => {
              if (res) {
                // It seems that attachments cant be deleted after topic is deleted. "Invalid attachment delete request: No such attachment"
                // attachments.length > 0 &&
                //   attachments.forEach((att) => {
                //     attachmentFetchBuilder<'delete'>({ name: 'delete', queryParams: [att.id, undefined] });
                //   });
                reloadTopics();
                setDeleteConfirmation(null);
              }
            });
          },
        }}
      />
      <AttachmentDialog attachmentId={openAttachmentId} topic={topic} setOpenAttachmentId={setOpenAttachmentId} />
    </>
  );
};

interface IGroupMessageOpen {
  t: TTOPIC;
  fm: (id: string) => string;
  reloadTopics: () => void;
}

export const GroupMessageItem = ({
  open,
  setOpen,
  topic,
  reloadTopics,
  color,
}: {
  open: boolean;
  setOpen: () => void;
  topic: TTOPIC;
  reloadTopics: () => void;
  color?: 'warning' | 'gray';
}) => {
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  return (
    <BlockWrapper
      collapse={{
        open,
        collapseHandler: () => setOpen(),
      }}
      color={color || 'warning'}
      title={<GroupMessageHeader t={topic} fm={fm} color={color === 'gray' ? 'gray' : 'yellow'} />}
      contentMargin={2}
      compact
    >
      <GroupMessageOpen t={topic} fm={fm} reloadTopics={reloadTopics} />
    </BlockWrapper>
  );
};

const GroupMessages = () => {
  const [openTopicId, setOpenTopicId] = React.useState<string | null>(null);

  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  const [loadingTopicsTime, setLoadingTopicsTime] = React.useState<boolean | number>(false);
  const dispatch = useAppDispatch();
  const session = useAppSelector(s => s.session.data);

  const reloadTopics = () => {
    setOpenTopicId(null);
    setLoadingTopicsTime(false);
    session?.orgid &&
      void dispatch(messagingActions.loadTopics()).then(() => {
        setTimeout(() => {
          setLoadingTopicsTime(Date.now());
        }, 500);
      });
  };

  React.useEffect(() => {
    reloadTopics();
  }, []);

  const topics = useAppSelector(s => s.messaging.topics);
  const groupTopics = [...(topics || [])].filter(to => to.targetPatients.length > 1);

  const activeGroupTopics = groupTopics
    .filter(thr => !thr.deletionTimestamp)
    .filter(t => !t.closureTimestamp || Date.now() < t.closureTimestamp)
    .toSorted((a, b) => {
      const aDate = a.creationTimestamp;
      const bDate = b.creationTimestamp;
      return bDate - aDate;
    });

  const inactiveGroupTopics = groupTopics
    .filter(thr => !thr.deletionTimestamp)
    .filter(t => t.closureTimestamp && Date.now() >= t.closureTimestamp)
    .toSorted((a, b) => {
      const aDate = a.creationTimestamp;
      const bDate = b.creationTimestamp;
      return bDate - aDate;
    });
  return (
    <>
      <RoundBox style={{ padding: '1rem' }}>
        <FilterField title={fm('messaging.filterByDate')} type="row">
          <ConnectedPartialDate type="PartialDate" name="filterDateStart" editing />
        </FilterField>
      </RoundBox>
      <div style={{ marginTop: '2rem' }}>
        {!loadingTopicsTime ? (
          <Container justifyContent={'center'}>
            <Item>
              <CircularProgressLocal />
            </Item>
          </Container>
        ) : (
          <div>
            {activeGroupTopics.map(topic => (
              <div key={topic.id} style={{ marginBottom: '1rem' }}>
                <GroupMessageItem
                  open={openTopicId === topic.id}
                  setOpen={() => setOpenTopicId(topic.id)}
                  topic={topic}
                  reloadTopics={reloadTopics}
                />
              </div>
            ))}
            {inactiveGroupTopics.length > 0 && (
              <div>
                <h3 style={{ color: colors.primary, margin: '2rem 0 1rem 0' }}>
                  {fm('messaging.completedGroupMessages')}
                </h3>
                {inactiveGroupTopics.map(topic => (
                  <div key={topic.id} style={{ marginBottom: '1rem' }}>
                    <BlockWrapper
                      collapse={{
                        open: openTopicId === topic.id,
                        collapseHandler: () => {
                          setOpenTopicId(openTopicId === topic.id ? null : topic.id);
                        },
                      }}
                      color="light"
                      title={<GroupMessageHeader t={topic} fm={fm} />}
                      contentMargin={2}
                      compact
                    >
                      <GroupMessageOpen t={topic} fm={fm} reloadTopics={reloadTopics} />
                    </BlockWrapper>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default GroupMessages;
